// bootstrap for build
import { createBrowserHistory } from 'history';
import { Sentry } from 'lib/sentry/core';
import React from 'react';
import ReactDOM from 'react-dom';
import { scan } from 'react-scan';
import { CONFIG as SHARED_CONFIG } from 'shared/config';
import { ls } from 'shared/utils/ls';
import packageJson from '../package.json';
import './index.css';
import { initializeSentry } from './lib/sentry';

// Create browser history
const history = createBrowserHistory();

// Advanced usage with all options
const initSentry = () => {
  initializeSentry({
    dsn: process.env.REACT_APP_SENTRY_DSN as string,

    // Router integration for React Router v4-5
    routerArgs: {
      history,
    },

    // Environment and release info
    environment: process.env.REACT_APP_ENV,
    release: packageJson.version,

    // Custom error handling
    beforeSend: (event, hint) => {
      const error = hint.originalException as Error;

      // Don't report navigation cancellation errors
      if (error && error.message && error.message.includes('Navigation was cancelled')) {
        return null;
      }

      // Tag all network errors for easier filtering
      if (error && error.message && error.message.includes('Network Error')) {
        event.tags = { ...event.tags, errorType: 'network' };
      }

      return event;
    },
  });
};

/**
 * List of remote modules that need to be loaded
 * These modules are fetched from the remote container
 */
const REMOTE_MODULES = [
  'VideoEditor',
  'Calendar',
  'ShareableProfile',
  'PhotoEditorV2',
  'CompositeCard',
  'MediaGallery',
  'FileDropZone',
] as const;

type RemoteModule = (typeof REMOTE_MODULES)[number];

/**
 * Interface for the manifest response
 */
interface ManifestResponse {
  metaData: {
    remoteEntry: {
      name: string;
    };
    publicPath: string;
  };
}

/**
 * Fetch manifest and get remote entry details
 */
const fetchManifest = async (manifestUrl: string): Promise<{ entryPath: string }> => {
  try {
    const response = await fetch(manifestUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch manifest: ${response.statusText}`);
    }

    const manifest: ManifestResponse = await response.json();
    const entryPath = `${manifest.metaData.publicPath}${manifest.metaData.remoteEntry.name}`;

    return { entryPath };
  } catch (error) {
    console.error('Error fetching manifest:', error);
    throw error;
  }
};

/**
 * Configuration constants
 */
const CONFIG = {
  INIT_TIMEOUT: 5000, // 5 seconds timeout for initialization
  RETRY_ATTEMPTS: 3, // Number of retries for loading modules
  RETRY_DELAY: 1000, // Delay between retries in milliseconds
} as const;

/**
 * Initialize React Scan in development mode
 */
const initReactScan = () => {
  const isLocal = process.env.REACT_APP_ENV === 'local';
  const isClient = typeof window !== 'undefined';

  if (isClient && isLocal) {
    scan({
      enabled: process.env.REACT_APP_REACT_SCAN_ENABLE === 'true',
      log: false,
    });
  }
};

/**
 * Initialize global error listeners
 * Captures both script errors and unhandled promise rejections
 */
const initErrorListener = () => {
  const errorHandler = (event: ErrorEvent | PromiseRejectionEvent) => {
    const errorMessage = event instanceof ErrorEvent ? 'Script loading error:' : 'Unhandled promise rejection:';
    console.error(errorMessage, event);
    if (SHARED_CONFIG.isProduction) {
      const { getLS } = ls();
      const user = getLS('user') ? JSON.parse(getLS('user')) : undefined;
      let defaultUsers = {};
      if (user) {
        defaultUsers = {
          user: {
            name: user.attributes.full_name,
            email: user.attributes.email,
          },
        };
      }
      Sentry.showReportDialog({
        ...defaultUsers,
        eventId: Sentry.lastEventId(),
        onLoad: () => {
          if (user) {
            const sentryCrashReportWrapper = document.querySelector('.sentry-error-embed-wrapper');
            const name = sentryCrashReportWrapper?.querySelector('input[name="name"]');
            // Check if the upload button already exists to avoid duplication
            if (!name) {
              return;
            }
            const email = sentryCrashReportWrapper?.querySelector('input[name="email"]');
            // Check if the upload button already exists to avoid duplication
            if (!email) {
              return;
            }
            name.setAttribute('disabled', 'true');
            email.setAttribute('disabled', 'true');
          }
        },
        onClose: () => {
          window.location.href = '/talent/dashboard';
        },
      });
    }
  };

  window.addEventListener('error', errorHandler);
  window.addEventListener('unhandledrejection', errorHandler);

  // Return cleanup function
  return () => {
    window.removeEventListener('error', errorHandler);
    window.removeEventListener('unhandledrejection', errorHandler);
  };
};

/**
 * Load a single remote module with retries
 */
const loadRemoteModule = async (moduleName: RemoteModule, attempt = 1): Promise<void> => {
  const isLocal = process.env.REACT_APP_ENV === 'local';

  try {
    isLocal && console.log(`Attempting to load ${moduleName} (attempt ${attempt})...`);

    const factory = await window.remote.get(`./${moduleName}`);
    if (!factory) {
      throw new Error(`Factory for ${moduleName} not found`);
    }

    await factory();
    isLocal && console.log(`Successfully loaded ${moduleName}`);
  } catch (err) {
    if (attempt < CONFIG.RETRY_ATTEMPTS) {
      console.warn(`Retrying ${moduleName} load... (${attempt}/${CONFIG.RETRY_ATTEMPTS})`);
      await new Promise((resolve) => setTimeout(resolve, CONFIG.RETRY_DELAY));
      return loadRemoteModule(moduleName, attempt + 1);
    }
    console.error(`Failed to load ${moduleName}:`, err);
    throw err;
  }
};

/**
 * Initialize all remote modules
 * Sets up the remote container and loads all modules
 */
const initRemoteModules = async (): Promise<void> => {
  const remoteUrl = process.env.REACT_APP_SHARED_COMPONENT_URL;
  if (!remoteUrl) {
    throw new Error('REACT_APP_SHARED_COMPONENT_URL is not defined');
  }

  try {
    // Initialize webpack sharing
    await Promise.race([
      __webpack_init_sharing__('default'),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Initialization timeout')), CONFIG.INIT_TIMEOUT)),
    ]);

    // Fetch manifest and get remote entry path
    const manifestUrl = `${remoteUrl}/mf-manifest.json`;
    const { entryPath } = await fetchManifest(manifestUrl);

    // Load remote entry script
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = entryPath;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load remote entry'));
      document.head.appendChild(script);
    });

    // Load all modules in parallel
    await Promise.all(REMOTE_MODULES.map((moduleName) => loadRemoteModule(moduleName)));
  } catch (err) {
    console.error('Failed to initialize remote modules:', err);
    throw err;
  }
};

/**
 * Render the application
 */
const render = async () => {
  try {
    const { default: App } = await import('./App');
    const rootElement = document.getElementById('root');

    if (!rootElement) {
      throw new Error('Failed to find root element');
    }

    ReactDOM.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>,
      rootElement,
    );
  } catch (error) {
    console.error('Render error:', error);
    const rootElement = document.getElementById('root');

    if (rootElement) {
      ReactDOM.render(
        <div className="error-container">
          <h1>Failed to initialize application</h1>
          <p>Please try refreshing the page</p>
          {process.env.REACT_APP_ENV === 'dev' && (
            <div>
              <pre style={{ whiteSpace: 'pre-wrap' }}>{error instanceof Error ? error.message : 'Unknown error'}</pre>
              <p>Remote URL: {process.env.REACT_APP_SHARED_COMPONENT_URL}</p>
            </div>
          )}
        </div>,
        rootElement,
      );
    }
  }
};

/**
 * Main bootstrap function
 * Initializes the application and renders it to DOM
 */
export async function bootstrap() {
  try {
    // Initialize sentry
    initSentry();

    // Initialize development tools
    initReactScan();
    const cleanupErrorListener = initErrorListener();

    // Wait for remote modules
    await initRemoteModules();

    // Render the application
    await render();

    return () => cleanupErrorListener();
  } catch (error) {
    console.error('Bootstrap error:', error);
    const rootElement = document.getElementById('root');

    if (rootElement) {
      ReactDOM.render(
        <div className="error-container">
          <h1>Failed to initialize application</h1>
          <p>Please try refreshing the page</p>
          {process.env.REACT_APP_ENV === 'dev' && (
            <div>
              <pre style={{ whiteSpace: 'pre-wrap' }}>{error instanceof Error ? error.message : 'Unknown error'}</pre>
              <p>Remote URL: {process.env.REACT_APP_SHARED_COMPONENT_URL}</p>
            </div>
          )}
        </div>,
        rootElement,
      );
    }
  }
}
